import React, { useEffect, useState } from 'react';
/**
 * `RenewalsPersonalDetail` captures customer personal details and address for
 * the renewals journey.
 *
 * Behaviour:
 * - Coordinates validation/update cycles for `PersonalDetailPage` and
 *   `AddressManager`.
 * - Applies call-centre specific readonly rules (name/title editability and
 *   email confirmation visibility).
 * - Navigates forward to vehicle details (renewals) or summary (call centre),
 *   and back to landing or quote.
 * - Copies address into single-payment billing address when it is empty.
 */

import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import PersonalDetailPage from '../../common/PersonalDetail/PersonalDetailPage';
import AddressManager from '../../common/Address/AddressManager';
import {
  Journey,
  PageAbsolutePath,
  PageRelativePath
} from '../../../consts/Common';
import { AddressDetails, PersonalDetailType } from '../../common/CommonTypes';
import PageHeader from '../../common/PageHeader';
import { ButtonBack } from '../../common/Button/ButtonBack';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import { useJourney } from '../Layout';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import { useTranslation } from 'react-i18next';
import { setCountryCode } from '../../common/HelperMethods';
import { useAppSelector } from '../../../app-state/Hooks';

const RenewalsPersonalDetail = (): JSX.Element => {
  const { t } = useTranslation();
  const { journey, setPersonalDetails, setAddressSinglePayment, onPageChange } =
    useJourney();

  const { personalDetail, singlePaymentDetails } = journey;
  const [validateAddress, setValidateAddress] = useState<boolean | null>(null);
  const [updateAddress, setUpdateAddress] = useState<boolean | null>(null);
  const [validatePersonalDetail, setValidatePersonalDetail] = useState<
    boolean | null
  >(null);
  const [updatePersonalDetail, setUpdatePersonalDetail] = useState<
    boolean | null
  >(null);
  const [goBack, setGoBack] = useState(false);
  const [goForward, setGoForward] = useState(false);

  const cms = useAppSelector((s) => s.content.customerPage);
  const appConfig = useAppSelector((s) => s.appConfig);
  const userToken = useAppSelector((s) => s.user.token);

  const token = journey.token || userToken;
  const navigate = useNavigate();

  const isCallCentre = appConfig.journeyType === Journey.CallCentre;
  const isNameReadonly = !isCallCentre;
  const isTitleEmpty = useAppSelector((s) => !s.customer.title?.trim());
  const isTitleReadonly = !isTitleEmpty && !isCallCentre;

  const emailTooltipHeader = t('personal-details.email-tooltip-header');

  const phoneNumberTooltipHeader = t(
    'personal-details.phone-number-tooltip-header'
  );

  const dobTooltipHeader = t('personal-details.dob-tooltip-header');
  const addressTitleText = t('personal-details.address-title-text');
  const addressLabelText = t('personal-details.address-label-text');
  const buttonNextLabel = t('personal-details.button-next-label');
  const buttonBackLabel = isCallCentre
    ? t('personal-details.button-back-label-back')
    : t('personal-details.button-back-label-logout');
  const callcentrePageTitle = t('personal-details.callcentre-page-title');

  const addressIsEmpty = (address: AddressDetails): boolean => {
    const isAddressEmpty =
      CommonValidation.isEmpty(address.addressLine1) &&
      CommonValidation.isEmpty(address.addressLine2) &&
      CommonValidation.isEmpty(address.townOrCity) &&
      CommonValidation.isEmpty(address.country) &&
      CommonValidation.isEmpty(address.region) &&
      CommonValidation.isEmpty(address.postCode);
    return isAddressEmpty;
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (updateAddress === false && goForward === true) {
      if (isCallCentre) navigate(PageRelativePath.Summary);
      else {
        navigate(PageRelativePath.VehicleDetails);
        onPageChange(PageAbsolutePath.Renewals.PersonalDetails, true);
      }
      if (addressIsEmpty(singlePaymentDetails?.billingAddress) === true) {
        setAddressSinglePayment(personalDetail.addressDetails);
      }
    }

    if (updateAddress === false && goBack === true) {
      if (isCallCentre) {
        navigate(PageRelativePath.Quote);
      } else {
        const path = setCountryCode(
          PageAbsolutePath.Renewals.Landing,
          appConfig.countryCode
        );
        navigate(path);
      }
      onPageChange(PageAbsolutePath.Renewals.PersonalDetails, false);
    }
  }, [updateAddress]);

  useEffect(() => {
    if (updatePersonalDetail === false) {
      setUpdateAddress(true);
    }
  }, [updatePersonalDetail]);

  useEffect(() => {
    if (isPageValid()) {
      setUpdatePersonalDetail(true);
    }
  }, [validatePersonalDetail, validateAddress]);

  const onPersonalDetailValidated = (isPDValid: boolean) => {
    if (isPDValid === true) {
      setValidatePersonalDetail(false);
    } else {
      setValidatePersonalDetail(null);
      setGoForward(false);
    }
  };

  const onAddressValidated = (isAddressValid: boolean) => {
    if (isAddressValid === true) {
      setValidateAddress(false);
    } else {
      setValidateAddress(null);
      setGoForward(false);
    }
  };

  const onAddressUpdate = (address: AddressDetails) => {
    const newPersonalDetail = {
      ...personalDetail,
      addressDetails: address
    };

    setPersonalDetails(newPersonalDetail);
    setUpdateAddress(false);
  };

  const onUpdatePersonalDetail = (pd: PersonalDetailType) => {
    setPersonalDetails(pd);
    setUpdatePersonalDetail(false);
  };

  const onBack = () => {
    setUpdatePersonalDetail(true);
    setGoBack(true);
  };

  const onNextClick = () => {
    setValidateAddress(true);
    setValidatePersonalDetail(true);
    setGoForward(true);
  };

  const isPageValid = (): boolean => {
    const isValid =
      validatePersonalDetail === false && validateAddress === false;

    return isValid;
  };

  return (
    <>
      <Helmet>
        <title>{t('page-title.personal-details')}</title>
      </Helmet>

      <div>
        {appConfig.journeyType === Journey.CallCentre ? (
          <h3>{callcentrePageTitle}</h3>
        ) : (
          <PageHeader
            title={cms.title}
            description={cms.description}
            information={cms.information}
            warning={cms.warning}
            descriptionClass='p2'
          />
        )}

        <PersonalDetailPage
          initialPersonalDetail={personalDetail}
          validate={validatePersonalDetail || false}
          token={token}
          isNameReadonly={isNameReadonly}
          isTitleReadonly={isTitleReadonly}
          update={updatePersonalDetail || false}
          onValidated={onPersonalDetailValidated}
          onUpdate={onUpdatePersonalDetail}
          showEmailConfirmation={!isCallCentre}
          dobTooltipHeader={dobTooltipHeader}
          emailTooltipHeader={emailTooltipHeader}
          phoneNumberTooltipHeader={phoneNumberTooltipHeader}
          isDoBOptional={cms.isDateOfBirthOptional}
        />

        <div className='control'>
          <AddressManager
            findAddressTitle={addressTitleText}
            initialAddress={personalDetail?.addressDetails}
            update={updateAddress || false}
            validate={validateAddress || false}
            token={token}
            findAddressLabel={addressLabelText}
            onAddressUpdate={onAddressUpdate}
            onAddressValidated={onAddressValidated}
            allowManualEntry
          />
        </div>
        <div className='flex-row form-nav'>
          <ButtonBack
            name='btnBack'
            onClick={onBack}
            text={`< ${buttonBackLabel}`}
          />
          <ButtonContinue
            name='btn-next-step'
            text={buttonNextLabel}
            onClick={onNextClick}
          />
        </div>
      </div>
    </>
  );
};

export default RenewalsPersonalDetail;
